import { useState, useEffect } from 'react';
import { useRemote } from '../../../Utils/Utils';
import { Edit2, Plus } from 'react-feather';
import Page, { PageContentBanner, PageContentBodyGrid } from '../../Shared/Page/Page';
import PageContentToggle from '../../../components/Menus/PageContentToggle';
import PageGridDivider from '../../Shared/Page/PageGridDivider';
import Table from '../../../components/Table/Table';
import Button from '../../../components/Buttons/Button';
import PageGridItem from '../../Shared/Page/PageGridItem';
import SimpleEditor from '../../../contents/SimpleEditor';
import AdminCompanyDisclaimersPage from './AdminCompanyDisclaimersPage';
import LoadingIcon from '../../../components/LoadingIcon';
import Tooltip from '../../../components/popups/Tooltip';


const AdminCompanyDisclaimerList = () => {
	const remote = useRemote();
	const thirdPartyHeaders = ["Name", "Disclaimer"];

	const [selectedTab, setSelectedTab] = useState("Company")
	const [editMode, setEditMode] = useState(false)
	const [disclaimerNotes, setDisclaimerNotes] = useState([])
	const [originalDisclaimerNotes, setOriginalDisclaimerNotes] = useState([])
	const [disabled, setDisabled] = useState(true)
	const [indexesModified, setIndexesModified] = useState([])
	const [changesCancelled, setChangesCancelled] = useState(false)
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		remote.get("/disclaimers/").then(disclaimerNotes => {
			setDisclaimerNotes(disclaimerNotes.sort((a, b) => a.name.localeCompare(b.name)));
			setOriginalDisclaimerNotes(disclaimerNotes);
		})
	}, [])

	function saveChanges() {
		setSaving(true);
		const stringified = disclaimerNotes.map(d => {
			if (d.disclaimer && d.disclaimer !== null && d.disclaimer !== "") {
				if (typeof d.disclaimer === "string") {
					if (d.disclaimer.startsWith("[") && d.disclaimer.endsWith("]")) {
						return { ...d, disclaimer: d.disclaimer }
					} else {
						return d
						return { ...d, disclaimer: JSON.stringify([{ type: 'text', children: [{ text: d.disclaimer }] }]) }
					}
				} else {
					return { ...d, disclaimer: JSON.stringify(d.disclaimer) }
				}
			} else {
				return { ...d, disclaimer: JSON.stringify([{ type: 'text', children: [{ text: "" }] }]) }
			}
		})
		remote.put("/disclaimers/", stringified).then(() => {
			setDisclaimerNotes(stringified);
			setOriginalDisclaimerNotes(stringified);
			setDisabled(true)
			setEditMode(false);
			setSaving(false);
		})
	}

	function cancelChanges() {
		setDisclaimerNotes(originalDisclaimerNotes);
		setIndexesModified([]);
		setEditMode(false)
		setDisabled(true);
		setChangesCancelled(true)
	}

	function editDisclaimers(value, index) {
		const updatedDisclaimers = disclaimerNotes.map((d, idx) => {
			if (index === idx) {
				if (originalDisclaimerNotes[idx].disclaimer !== value) {
					setDisabled(false);
					setIndexesModified((prev) => [...prev, idx]);
				} else {
					if (indexesModified.includes(idx)) {
						const updateIndexes = indexesModified.filter((i) => i !== idx);
						setIndexesModified(updateIndexes);
						if (updateIndexes.length === 0) {
							setDisabled(true);
						}
					}
				}
				return { ...d, disclaimer: value };
			} else return d
		});
		setDisclaimerNotes(updatedDisclaimers);
	}

	const labels = [
		{ label: "Company", active: selectedTab === "Company", onClick: () => setSelectedTab("Company") },
		{ label: "Note Disclaimers", active: selectedTab === "Note Disclaimers", onClick: () => setSelectedTab("Note Disclaimers") },
	]

	const thirdPartyDataMap = (data) =>
		data &&
		data.map((d, idx) => {
			return (
				<tr key={idx} className="cursor-pointer">
					<td style={{ verticalAlign: 'top', lineHeight: 1.2 }}>{d.name}</td>
					<td style={{ verticalAlign: 'top', lineHeight: 1.2, whiteSpace: 'pre-line' }}>
						<div style={{ fontWeight: 600, paddingBottom: '1rem'}}>
							{d.footnoteSymbol
								? <Tooltip label={<span><sup style={{ fontSize: '66%' }}>{d.footnoteSymbol}</sup>{d.title}</span>}>
									<span>{d.title}</span>
								</Tooltip>
								: d.title}
						</div>
						<SimpleEditor
							onChange={(e, idx) => editDisclaimers(e, idx)}
							load={disclaimerNotes[idx].disclaimer}
							passedClassName={"no-padding-editor"}
							index={idx}
							readOnly={!editMode}
							changesCancelled={changesCancelled}
							setChangesCancelled={setChangesCancelled}
						/>
					</td>
				</tr>
			);
		});

	return (
		<Page noOverflownY>
			<PageContentBanner divider gap='m'>
				<Button disabled icon={<Plus />}>New Disclaimer</Button>
				{selectedTab === 'Note Disclaimers'
					? editMode ? <>
						<Button disabled={disabled || saving} onClick={() => disabled ? null : saveChanges()}>Save Changes</Button>
						<Button disabled={saving} variant='secondary' onClick={() => cancelChanges()}>Cancel</Button>
						{saving && <LoadingIcon size={24}/>}
					</>
						: <Button onClick={() => setEditMode(true)} icon={<Edit2 />}>Edit Disclaimers</Button>
					: ""
				}
			</PageContentBanner>
			<PageContentBodyGrid id="admin-disclaimer-page" paddingTop='1.5rem'>
				<PageGridItem gap='m' direction='col' col="1 / span 12" >
					<PageContentToggle style={{ overflowX: 'auto', scrollbarWidth: 'none' }} labels={labels} />
					<PageGridDivider />
				</PageGridItem>
				{selectedTab === 'Company'
					? <AdminCompanyDisclaimersPage />
					: <PageGridItem colSpan="12" style={{ overflowX: 'auto', scrollbarWidth: 'none', height: 'calc(100vh - 15rem)' }} >
						<Table headers={thirdPartyHeaders} dataMap={thirdPartyDataMap(disclaimerNotes)} colWidths={['30%', '70%']} />
					</PageGridItem>
				}
			</PageContentBodyGrid>
		</Page>
	);
}

export default AdminCompanyDisclaimerList;